import React, { useContext, useEffect, useState } from "react";
import UserContext from "context/User";
import { orgHasCalculator } from "helpers/calculatorAccess";
import { createColumnHelper } from "@tanstack/react-table";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Badge, Box, Flex, Image } from "theme-ui";
import { PillWrapper, InviteWrapper } from "./styled";
import { getUsers, createUser, updateUser, deleteUser, resendInvite } from "api/Clients/Users";
import AnnouncementHistory from "components/Announcement/AnnouncementHistory";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { useToast } from "components/toast";
import TableData from "components/ui/TableData";
import PlusIcon from "components/svg/icons/PlusIcon";
import { AvatarIcon, CheckMarkIcon, CrownIcon, QuestionMarkIcon, TrashIcon, UsersIcon } from "components/svg";
import EllipsisIcon from "components/svg/icons/EllipsisIcon";
import DropDownMenu from "components/forms/DropDown/DropDownMenu";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import Span from "components/typography/Span";
import moment from "moment-timezone";
import SelectSearch from "components/forms/SelectSearch";
import { getRegions } from "api/Clients/Regions";
import { getGroups } from "api/Clients/Groups";
import { getDistinctRegions } from "api/LocationClientProfiles";
import { updateUser as updateInternalUser } from "api/Users";
import MultiText from "components/forms/MultiText";
import Checkbox from "components/forms/Checkbox";
import { Spinner } from "theme-ui";
import { PlaneIcon } from "components/svg";

type UserRow = {
  name?: string;
  groups?: { name: string; color: string }[];
  regions?: { name: string; color: string }[];
  primaryUser?: { status?: number; id?: string; lastActiveAt?: string; createdAt?: string };
  companyAdmin?: boolean;
  contactEmail?: string;
  logo?: string;
  actions?: any;
};

export default function Users() {
  const columnHelper = createColumnHelper<UserRow>();
  const [refreshData, setRefreshData] = useState<boolean>(false);

  const [edit, setEdit] = useState<any>({});
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [createModal, toggleCreateModal] = useState<boolean>(false);
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [siteRegionOptions, setSiteRegionOptions] = useState<Array<{ value: string; label: string }>>([]);
  // The org has the Mulch Calculator: offer the per-person switch.
  const { user: me } = useContext(UserContext) as any;
  const orgCalculator = orgHasCalculator(me);

  useEffect(() => {
    // §4.8 #3 — fetch distinct site regions already in use, to seed the
    // MultiText picker's suggestions. Free-text so admins can still
    // type new ones; this just spares them retyping existing labels.
    // Staff-only list (the picker is staff-only); skip the call for an org admin.
    if (me?.accessLevel !== "isCompany") return;
    (async () => {
      const regions = await getDistinctRegions();
      setSiteRegionOptions(regions.map((r) => ({ value: r, label: r })));
    })();
  }, [me?.accessLevel]);

  const toast = useToast();
  const formatDate = (dateString: string): string => {
    return moment(dateString).format("MMM D, YYYY");
  };

  const resendUserInvite = async (user: any) => {
    const res = await resendInvite({ data: user });
    if (res?.success) {
      toast.add("Invite resent.", "var(--theme-ui-colors-success)");
      setRefreshData(true);
      setTimeout(() => {
        setRefreshData(false);
      }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error resending invite.", "var(--theme-ui-colors-danger)");
    }
  };

  const columns = [
    columnHelper.accessor((row: any) => row.name, {
      id: "name",
      cell: (info: any) => {
        const userData = info.row.original?.primaryUser?.data || [];
        const userLogo = userData.find((d: any) => d.key === "logo")?.value;
        return (
          <Flex sx={{ gap: "1rem", alignItems: "center" }}>
            {userLogo ? (
              <Image width="40px" height="40px" src={userLogo} sx={{borderRadius:"6px"}}/>
            ) : (
              <AvatarIcon fill={"#BFBFBF"} width={"40px"} />
            )}
            <Box>
              <Span color="#000" fontWeight="500">{info.getValue()}</Span>
              <Span color="var(--theme-ui-colors-base_500)" display="block">{info.row.original?.contactEmail}</Span>         
            </Box>
            {info.row.original?.companyAdmin && (
              <Box sx={{ marginLeft: "auto" }}>
                <Badge as="span" sx={{ gap: "10px" }} variant="caution">
                  <span>Admin</span> <CrownIcon fill={"#CAA11C"} width="14px" />
                </Badge>
              </Box>
            )}
          </Flex>
        );
      },
      header: () => <span>User Info</span>,
      enableSorting: false,
      meta: {
        style: { width: "400px" },
      },
    }),
    columnHelper.accessor((row: any) => row.groups, {
      id: "groups",
      cell: (info: any) => (
        <PillWrapper>
          {info.row.original.groups.map((item: any, idx: number) => (
            <Flex key={idx}>
              <Box
                sx={{
                  backgroundColor: `${item.color}25 !important`,
                  borderColor: item.color,
                  color: item.color,
                  fontSize: 12,
                  padding: "6px 13px",
                  borderRadius: 50,
                  borderWidth: 1,
                  borderStyle: "solid",
                }}
              >
                {item.name}
              </Box>
            </Flex>
          ))}
        </PillWrapper>
      ),
      header: () => <span>Groups</span>,
      enableSorting: false,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row.regions, {
      id: "regions",
      cell: (info: any) => (
        <PillWrapper>
          {info.row.original.regions.map((item: any, idx: number) => (
            <Flex key={idx}>
              <Box
                sx={{
                  backgroundColor: `${item.color}25 !important`,
                  borderColor: item.color,
                  color: item.color,
                  fontSize: 12,
                  padding: "6px 13px",
                  borderRadius: 50,
                  borderWidth: 1,
                  borderStyle: "solid",
                }}
              >
                {item.name}
              </Box>
            </Flex>
          ))}
        </PillWrapper>
      ),
      header: () => <span>Regions</span>,
      enableSorting: false,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.primaryUser?.status, {
      id: "status",
      cell: (info) => {
        const [inviteText, setInviteText] = React.useState("Invite Sent");
        const [isProcessing, setIsProcessing] = React.useState(false);

        const handleMouseEnter = () => {
          if (!isProcessing) setInviteText("Resend Invite");
        };

        const handleMouseLeave = () => {
          if (!isProcessing) setInviteText("Invite Sent");
        };

        const handleClick = async () => {
          setIsProcessing(true);
          try {
            await resendUserInvite(info.row.original);
          } catch (error: any) {
            console.error("Failed to resend invite:", error);
          } finally {
            setIsProcessing(false);
          }
        };

        switch (info.getValue()) {
          case 1:
            return (
              <Badge as="span" variant="success">
                Active
              </Badge>
            );
          case 2:
            return (
              <InviteWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>
                <Badge as="span" sx={{ gap: "10px" }} variant="info">
                  <span>{inviteText}</span>
                  {!isProcessing ? <PlaneIcon fill="#104E9A" width="14px" /> : <Spinner sx={{ color: "#104E9A" }} size={14} />}
                </Badge>
              </InviteWrapper>
            );
          default:
            return (
              <Badge as="span" variant="danger">
                Inactive
              </Badge>
            );
        }
      },
      header: () => <span>Status</span>,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.primaryUser?.lastActiveAt, {
      id: "lastActiveAt",
      cell: (info: any) => (info.getValue() !== null ? formatDate(info.getValue()) : null),
      header: () => <span>Last Active</span>,
      meta: {
        style: { width: "130px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.primaryUser?.createdAt, {
      id: "createdAt",
      cell: (info: any) => formatDate(info.getValue()),
      header: () => <span>Date Added</span>,
      meta: {
        style: { width: "130px" },
      },
    }),
    columnHelper.accessor("actions", {
      id: "actions",
      cell: (info: any) => {
        const items = [
          { label: "Edit", onClick: () => openEditModal(info.row.original) },
          { label: "Deactivate", onClick: () => submitDelete(info.row.original), style: { color: "var(--theme-ui-colors-danger)" } },
        ];
        return (
          <Box>
            <DropDownMenu toggleComponent={<EllipsisIcon fill={"#818181"} width="20px" height="20px" />} items={items} />
          </Box>
        );
      },
      header: () => null,
      enableSorting: false,
      meta: {
        style: { width: "10px" },
      },
    }),
  ];

  const openEditModal = (edit: any) => {
    setEdit(edit);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEdit(null);
    setEditModalOpen(false);
  };

  const submitUpdate = async (values: any, actions: any) => {
    const update = {
      firstName: values.firstName,
      lastName: values.lastName,
      contactEmail: values.contactEmail,
      groups: values.groups,
      regions: values.regions,
      companyAdmin: values.companyAdmin,
      // Mulch Calculator for this person (admins always have it).
      ...(orgCalculator ? { portalCalculatorAccess: values.portalCalculatorAccess === true } : {}),
    };

    const res = await updateUser({ data: { id: edit.id }, update });

    // §4.8 #3 — clientAssignedRegions lives on the User entity, not
    // Client, so it needs its own PATCH through the internal /users
    // endpoint (the api/Clients/Users update above touches only the
    // Client row). Only fire when we have a linked primaryUser AND the
    // value actually changed — avoids a no-op round trip on every save.
    const initialSiteRegions: string[] = edit?.primaryUser?.clientAssignedRegions ?? [];
    const nextSiteRegions: string[] = Array.isArray(values.siteRegions) ? values.siteRegions : [];
    const changed =
      initialSiteRegions.length !== nextSiteRegions.length ||
      initialSiteRegions.some((r) => !nextSiteRegions.includes(r)) ||
      nextSiteRegions.some((r) => !initialSiteRegions.includes(r));
    if (me?.accessLevel === "isCompany" && edit?.primaryUser?.id && changed) {
      await updateInternalUser({
        data: { id: edit.primaryUser.id },
        update: { clientAssignedRegions: nextSiteRegions.length ? nextSiteRegions : null },
      });
    }

    if (res?.id) {
      toast.add("User updated.", "var(--theme-ui-colors-success)");
      setRefreshData(true);
      setTimeout(() => {
        setRefreshData(false);
      }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error updating user.", "var(--theme-ui-colors-danger)");
    }
    closeEditModal();
  };

  const UpdateSchema = Yup.object().shape({
    contactEmail: Yup.string().email("Invalid email").required("Email is required."),
  });

  const toggleCreate = () => toggleCreateModal(!createModal);

  const submitCreate = async (values: any) => {
    const create = {
      firstName: values.firstName,
      lastName: values.lastName,
      contactEmail: values.contactEmail,
      groups: values.groups,
      regions: values.regions,
      companyAdmin: values.companyAdmin,
      // Mulch Calculator for this person (admins always have it).
      ...(orgCalculator ? { portalCalculatorAccess: values.portalCalculatorAccess === true } : {}),
    };

    const res = await createUser({ create });
    if (res?.id) {
      toast.add("User created.", "var(--theme-ui-colors-success)");
      toggleCreateModal(false);
      setRefreshData(true);
      setTimeout(() => {
        setRefreshData(false);
      }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error creating user.", "var(--theme-ui-colors-danger)");
    }
  };

  const CreateSchema = Yup.object().shape({
    firstName: Yup.string().required("This field is required."),
    lastName: Yup.string().required("This field is required."),
    contactEmail: Yup.string().email("Invalid email").required("This field is required."),
  });

  const confirmDelete = async (del: any) => {
    setConfirmSubmit(true);
    const res = await deleteUser({ data: { id: del.id } });
    if (res?.success) {
      toast.add("User deleted.", "var(--theme-ui-colors-success)");
      setRefreshData(true);
      setTimeout(() => {
        setRefreshData(false);
      }, 100);
    } else {
      toast.add(res?.message ? res.message : "Error deleting user.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const submitDelete = async (del?: any) => {
    setConfirmModal(
      <BasicModal
        title="Deactivate user?"
        size={"sm"}
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton type="button" scheme="danger" onClick={() => confirmDelete(del)} $submitting={confirmSubmit} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
            {!confirmSubmit && <TrashIcon fill={"#fff"} />}
            {"Deactivate"}
          </BasicButton>
        }
      >
        <P>
          Are you sure you want to deactivate{" "}
          <strong style={{ color: "var(--theme-ui-colors-black)" }}>
            {del?.firstName} {del?.lastName}
          </strong>
          ?
        </P>
      </BasicModal>
    );
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | User";
  }, []);

  return (
    <>
      <Header></Header>
      <Page title="Users" subtitle="Manage user access">
        <TableData
          api={getUsers}
          columns={columns}
          refresh={refreshData}
          actions={[
            <BasicButton key="1" onClick={toggleCreate} $iconHeight="10px">
              <PlusIcon fill="#fff" />
              Add user
            </BasicButton>,
          ]}
          isFixed={true}
          title={"All Users"}
        />
      </Page>

      <Modal isOpen={editModalOpen} toggle={closeEditModal} centered={true} size="sm">
        <Formik
          initialValues={{
            ...(edit || {}),
            ...(edit?.user || {}),
            regions: edit?.regions?.map((obj: { id: string }) => obj.id),
            groups: edit?.groups?.map((obj: { id: string }) => obj.id),
            siteRegions: edit?.primaryUser?.clientAssignedRegions || [],
            portalCalculatorAccess: Number(edit?.portalCalculatorAccess) === 1 || edit?.portalCalculatorAccess === true,
          }}
          onSubmit={submitUpdate}
          validationSchema={UpdateSchema}
        >
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={closeEditModal}>
                  <UsersIcon fill={"#000"} width="18px" height="18px" margin="0 1rem 0 0" />
                  <Box>
                    <Box
                      sx={{
                        fontSize: "16px",
                        fontWeight: 500,
                        fontFamily: "Rubik",
                        color: "base_800",
                      }}
                    >
                      Edit user
                    </Box>
                    <Box
                      sx={{
                        fontSize: "12px",
                        fontFamily: "Poppins",
                        color: "dkgrey",
                      }}
                    >{`Edit a user’s details.`}</Box>
                  </Box>
                </ModalHeader>
                <ModalBody>
                  <TextInput
                    name="firstName"
                    label="First Name"
                    value={values.firstName}
                    onChange={(val: any) => setFieldValue("firstName", val)}
                    $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}
                  />
                  <TextInput
                    name="lastName"
                    label="Last Name"
                    value={values.lastName}
                    onChange={(val: any) => setFieldValue("lastName", val)}
                    $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}
                  />
                  <TextInput
                    name="contactEmail"
                    label="Email"
                    value={values.contactEmail}
                    onChange={(val: any) => setFieldValue("contactEmail", val)}
                    $errors={errors.contactEmail && submitCount > 0 ? errors.contactEmail : null}
                  />
                  <SelectSearch
                    api={getGroups}
                    name="groups"
                    label="Groups"
                    value={values?.groups}
                    keyLabel={["name"]}
                    keyValue="id"
                    preload
                    multiple
                    colorCoded={true}
                    onChange={(val: any) => setFieldValue("groups", val)}
                    $errors={errors?.groups && submitCount > 0 ? errors?.groups : null}
                  />
                  <SelectSearch
                    api={getRegions}
                    name="regions"
                    label="Regions"
                    value={Array.isArray(values?.regions) ? values?.regions : []}
                    keyLabel={["name"]}
                    keyValue="id"
                    preload
                    multiple
                    colorCoded={true}
                    onChange={(val: any) => setFieldValue("regions", val)}
                    $errors={errors?.regions && submitCount > 0 ? errors?.regions : null}
                  />
                  {/* Site region access is a STAFF restriction (its list and its
                      save are staff-only endpoints); an org admin cannot set it. */}
                  {me?.accessLevel === "isCompany" && <MultiText
                    name="siteRegions"
                    label="Location Region Access"
                    description="Maintenance Locations are filtered to these regions. Leave empty for full access."
                    value={Array.isArray(values?.siteRegions) ? values?.siteRegions : []}
                    options={siteRegionOptions}
                    onChange={(val: any) => setFieldValue("siteRegions", val || [])}
                  />}
                  <Checkbox
                    label="Grant Admin access"
                    checked={values.companyAdmin}
                    labelSize="14px"
                    sublabel="Admins have full access to manage users and settings."
                    onChange={(val) => {
                      setFieldValue("companyAdmin", val);
                    }}
                    $customStyles={{ marginBottom: "30px" }}
                  />
                  {orgCalculator && !values.companyAdmin && (
                    <Checkbox
                      label="Mulch Calculator"
                      checked={values.portalCalculatorAccess === true}
                      labelSize="14px"
                      sublabel="Lets this person measure your sites in the Mulch Calculator. Admins always have it."
                      onChange={(val) => setFieldValue("portalCalculatorAccess", val)}
                      $customStyles={{ marginBottom: "20px" }}
                    />
                  )}
                  <AnnouncementHistory userId={edit?.id} />
                </ModalBody>
                <ModalFooter>
                  <BasicButton outline={true} styles={{ margin: ".25rem 10px .25rem 0" }} type="button" onClick={closeEditModal} scheme="secondary">
                    Cancel
                  </BasicButton>
                  <BasicButton styles={{ margin: ".25rem 0 .25rem 0" }} type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                    Save User
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={createModal} toggle={toggleCreate} centered={true} size="sm">
        <Formik initialValues={{ color: "#000" }} onSubmit={submitCreate} validationSchema={CreateSchema}>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => {
            return (
              <Form>
                <ModalHeader toggle={toggleCreate}>
                  <UsersIcon fill={"#000"} width="18px" height="18px" margin="0 1rem 0 0" />
                  <Box>
                    <Box
                      sx={{
                        fontSize: "16px",
                        fontWeight: 500,
                        fontFamily: "Rubik",
                        color: "base_800",
                      }}
                    >
                      Add User
                    </Box>
                    <Box
                      sx={{
                        fontSize: "12px",
                        fontFamily: "Poppins",
                        color: "dkgrey",
                        fontWeight: 400,
                      }}
                    >
                      Add a new user to your company.
                    </Box>
                  </Box>
                </ModalHeader>
                <ModalBody>
                  {/* {JSON.stringify(errors)} */}
                  <TextInput
                    name="firstName"
                    label="First Name"
                    value={values.firstName}
                    onChange={(val: any) => setFieldValue("firstName", val)}
                    $errors={errors.firstName && submitCount > 0 ? errors.firstName : null}
                  />
                  <TextInput
                    name="lastName"
                    label="Last Name"
                    value={values.lastName}
                    onChange={(val: any) => setFieldValue("lastName", val)}
                    $errors={errors.lastName && submitCount > 0 ? errors.lastName : null}
                  />
                  <TextInput
                    name="contactEmail"
                    label="Email"
                    value={values.contactEmail}
                    onChange={(val: any) => setFieldValue("contactEmail", val)}
                    $errors={errors.contactEmail && submitCount > 0 ? errors.contactEmail : null}
                  />
                  <SelectSearch
                    api={getGroups}
                    name="groups"
                    label="Groups"
                    value={values?.groups}
                    keyLabel={["name"]}
                    keyValue="id"
                    preload
                    multiple
                    colorCoded={true}
                    placeholder="Select groups"
                    onChange={(val: any) => setFieldValue("groups", val)}
                    $errors={errors?.groups && submitCount > 0 ? errors?.groups : null}
                  />
                  <SelectSearch
                    api={getRegions}
                    name="regions"
                    label="Regions"
                    value={values?.regions}
                    keyLabel={["name"]}
                    keyValue="id"
                    preload
                    multiple
                    colorCoded={true}
                    placeholder="Select regions"
                    onChange={(val: any) => setFieldValue("regions", val)}
                    $errors={errors?.regions && submitCount > 0 ? errors?.regions : null}
                  />
                  <Checkbox
                    label="Grant Admin access"
                    checked={values.companyAdmin}
                    labelSize="14px"
                    $customStyles={{ marginBottom: "30px" }}
                    sublabel="Admins have full access to manage users and settings."
                    onChange={(val) => {
                      setFieldValue("companyAdmin", val);
                    }}
                  />
                  {orgCalculator && !values.companyAdmin && (
                    <Checkbox
                      label="Mulch Calculator"
                      checked={values.portalCalculatorAccess === true}
                      labelSize="14px"
                      sublabel="Lets this person measure your sites in the Mulch Calculator. Admins always have it."
                      onChange={(val) => setFieldValue("portalCalculatorAccess", val)}
                      $customStyles={{ marginBottom: "20px" }}
                    />
                  )}
                </ModalBody>
                <ModalFooter>
                  <>
                    <BasicButton outline={true} styles={{ margin: ".25rem 10px .25rem 0" }} type="button" onClick={toggleCreate} scheme="secondary">
                      Cancel
                    </BasicButton>
                    <BasicButton styles={{ margin: ".25rem 0 .25rem 0" }} type="submit" $submitting={isSubmitting} $iconHeight="14px" $loadingColor="#fff" keepText={true}>
                      {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                      Save User
                    </BasicButton>
                  </>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      {confirmModal}
    </>
  );
}
